import { useEffect, useState, useCallback, useRef } from "react";
import api, { errMsg } from "../lib/api";
import { useI18n } from "../context/I18nContext";
import { useAuth } from "../context/AuthContext";
import { PageHeader, Empty } from "../components/common";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { Plus, Download, Upload, Pencil, Ban } from "lucide-react";

const EMPTY = {
  employee_number: "", name: "", department: "", position: "",
  email: "", phone: "", project: "", active: true,
};

export default function Personnel() {
  const { t } = useI18n();
  const { can } = useAuth();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [department, setDepartment] = useState("all");
  const [departments, setDepartments] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const fileRef = useRef(null);

  const canEdit = can("admin", "buyer", "warehouse");

  const load = useCallback(() => {
    const params = {};
    if (q) params.q = q;
    if (department !== "all") params.department = department;
    api.get("/personnel", { params }).then((r) => setRows(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [q, department]);

  const loadDepartments = useCallback(() => {
    api.get("/departments").then((r) => setDepartments(r.data)).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadDepartments(); }, [loadDepartments]);

  const save = async () => {
    if (!form.name.trim()) { toast.error(t("pn.name")); return; }
    try {
      if (editId) await api.put(`/personnel/${editId}`, form);
      else await api.post("/personnel", form);
      toast.success(t("common.saved"));
      setOpen(false); setForm(EMPTY); setEditId(null);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const openEdit = (r) => {
    setEditId(r.id);
    setForm({
      employee_number: r.employee_number || "",
      name: r.name || "",
      department: r.department || "",
      position: r.position || "",
      email: r.email || "",
      phone: r.phone || "",
      project: r.project || "",
      active: r.active !== false,
    });
    setOpen(true);
  };

  const openNew = () => {
    setEditId(null);
    setForm(EMPTY);
    setOpen(true);
  };

  const deactivate = async (r) => {
    if (!window.confirm(`${t("pn.deactivate")}: ${r.name}?`)) return;
    try {
      await api.delete(`/personnel/${r.id}`);
      toast.success(t("common.saved"));
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const downloadTemplate = async () => {
    const { data } = await api.get("/personnel-template");
    const blob = new Blob([data], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "personnel_template.csv";
    a.click();
  };

  const handleImport = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await api.post("/personnel-import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${t("pn.created")}: ${data.created} · ${t("pn.updated")}: ${data.updated}`);
      if (data.errors?.length) toast.warning(data.errors.slice(0, 3).join("\n"));
      load();
    } catch (err) { toast.error(errMsg(err)); }
    finally { if (fileRef.current) fileRef.current.value = ""; }
  };

  return (
    <div data-testid="personnel-page">
      <PageHeader title={t("pn.title")} />
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <Input
          data-testid="pn-search-input"
          placeholder={t("pn.searchPh")}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="max-w-xs rounded-none h-9 text-xs"
        />
        <Select value={department} onValueChange={setDepartment}>
          <SelectTrigger className="rounded-none h-9 w-56 text-xs" data-testid="pn-dept-filter">
            <SelectValue placeholder={t("pn.department")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">— {t("pn.department")} —</SelectItem>
            {departments.filter((d) => d.active !== false).map((d) => (
              <SelectItem key={d.id} value={d.name}>{d.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="ml-auto flex gap-2 flex-wrap">
          <Button size="sm" variant="outline" className="rounded-none text-xs font-bold"
                  onClick={downloadTemplate} data-testid="pn-template-btn">
            <Download size={12} className="mr-1" /> {t("pn.template")}
          </Button>
          {canEdit && (
            <>
              <Button size="sm" variant="outline" className="rounded-none text-xs font-bold"
                      onClick={() => fileRef.current?.click()} data-testid="pn-import-btn">
                <Upload size={12} className="mr-1" /> {t("pn.import")}
              </Button>
              <input ref={fileRef} type="file" accept=".csv,.xlsx" onChange={handleImport} className="hidden" data-testid="pn-import-input" />
              <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setForm(EMPTY); setEditId(null); } }}>
                <DialogTrigger asChild>
                  <Button size="sm" className="rounded-none text-xs font-bold" onClick={openNew} data-testid="pn-new-btn">
                    <Plus size={12} className="mr-1" /> {t("pn.new")}
                  </Button>
                </DialogTrigger>
                <DialogContent className="rounded-none max-w-2xl">
                  <DialogHeader>
                    <DialogTitle>{editId ? t("pn.edit") : t("pn.new")}</DialogTitle>
                  </DialogHeader>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-[10px] font-bold uppercase">{t("pn.empNumber")}</Label>
                      <Input value={form.employee_number} onChange={(e) => setForm({ ...form, employee_number: e.target.value })}
                             className="rounded-none h-9 text-xs" data-testid="pn-form-empnum" />
                    </div>
                    <div>
                      <Label className="text-[10px] font-bold uppercase">{t("pn.name")}</Label>
                      <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                             className="rounded-none h-9 text-xs" data-testid="pn-form-name" />
                    </div>
                    <div>
                      <Label className="text-[10px] font-bold uppercase">{t("pn.department")}</Label>
                      <Select value={form.department || "_none"} onValueChange={(v) => setForm({ ...form, department: v === "_none" ? "" : v })}>
                        <SelectTrigger className="rounded-none h-9 text-xs" data-testid="pn-form-dept">
                          <SelectValue placeholder={t("pn.department")} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="_none">—</SelectItem>
                          {departments.filter((d) => d.active !== false).map((d) => (
                            <SelectItem key={d.id} value={d.name}>{d.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-[10px] font-bold uppercase">{t("pn.position")}</Label>
                      <Input value={form.position} onChange={(e) => setForm({ ...form, position: e.target.value })}
                             className="rounded-none h-9 text-xs" data-testid="pn-form-position" />
                    </div>
                    <div>
                      <Label className="text-[10px] font-bold uppercase">{t("pn.email")}</Label>
                      <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                             className="rounded-none h-9 text-xs" data-testid="pn-form-email" />
                    </div>
                    <div>
                      <Label className="text-[10px] font-bold uppercase">{t("pn.phone")}</Label>
                      <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                             className="rounded-none h-9 text-xs" data-testid="pn-form-phone" />
                    </div>
                    <div>
                      <Label className="text-[10px] font-bold uppercase">{t("pn.project")}</Label>
                      <Input value={form.project} onChange={(e) => setForm({ ...form, project: e.target.value })}
                             className="rounded-none h-9 text-xs" data-testid="pn-form-project" />
                    </div>
                    <div className="flex items-end">
                      <label className="flex items-center gap-2 text-xs font-bold">
                        <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })}
                               data-testid="pn-form-active" />
                        {t("pn.active")}
                      </label>
                    </div>
                  </div>
                  <div className="flex justify-end mt-4">
                    <Button size="sm" className="rounded-none text-xs font-bold" onClick={save} data-testid="pn-form-save">
                      {t("common.save")}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </>
          )}
        </div>
      </div>

      {rows.length === 0 ? (
        <Empty text={t("common.noData")} />
      ) : (
        <div className="tbl-wrap">
          <table className="tbl" data-testid="pn-table">
            <thead>
              <tr>
                <th>{t("pn.empNumber")}</th>
                <th>{t("pn.name")}</th>
                <th>{t("pn.department")}</th>
                <th>{t("pn.position")}</th>
                <th>{t("pn.email")}</th>
                <th>{t("pn.phone")}</th>
                <th>{t("pn.project")}</th>
                <th>{t("pn.active")}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} data-testid={`pn-row-${r.id}`}>
                  <td className="font-bold">{r.employee_number || "—"}</td>
                  <td>{r.name}</td>
                  <td>{r.department || "—"}</td>
                  <td>{r.position || "—"}</td>
                  <td className="text-muted-foreground">{r.email || "—"}</td>
                  <td className="text-muted-foreground">{r.phone || "—"}</td>
                  <td>{r.project || "—"}</td>
                  <td>
                    <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 ${r.active ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"}`}>
                      {r.active ? t("pn.active") : t("pn.inactive")}
                    </span>
                  </td>
                  <td>
                    {canEdit && (
                      <div className="flex gap-1">
                        <Button size="sm" variant="ghost" className="h-7 px-2 rounded-none"
                                onClick={() => openEdit(r)} data-testid={`pn-edit-${r.id}`}>
                          <Pencil size={12} />
                        </Button>
                        {r.active && (
                          <Button size="sm" variant="ghost" className="h-7 px-2 rounded-none text-destructive"
                                  onClick={() => deactivate(r)} data-testid={`pn-deactivate-${r.id}`}>
                            <Ban size={12} />
                          </Button>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
